import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertCircle, Shield, CreditCard, FileText, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';

interface PendingApprovalBlockProps {
  children: React.ReactNode;
}

interface KYCData {
  nationalIdPassport: {
    type: 'national_id' | 'passport' | 'driving_license';
    number: string;
    issueDate: string;
    expiryDate: string;
    frontImage: File | null;
    backImage: File | null;
  };
  taxId: {
    type: 'pan' | 'ssn' | 'tax_id' | 'other';
    number: string;
    issueDate: string;
    expiryDate: string;
  };
}

const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions (max 800x800)
      const maxSize = 800;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Fallback to original file
          }
        },
        'image/jpeg',
        0.8 // 80% quality
      );
    };
    
    img.src = URL.createObjectURL(file);
  });
};

const uploadImageToFirebase = async (file: File, path: string) => {
  try {
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error(`File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size of 5MB`);
    }
    
    // Add timeout to prevent hanging (reduced to 15 seconds)
    const uploadPromise = new Promise<string>(async (resolve, reject) => {
      try {
        const storageRef = ref(storage, path);
        
        const snapshot = await uploadBytes(storageRef, file);
        
        const downloadURL = await getDownloadURL(storageRef);
        
        resolve(downloadURL);
      } catch (error) {
        reject(error);
      }
    });
    
    // Set 15 second timeout (reduced from 30)
    const timeoutPromise = new Promise<string>((_, reject) => {
      setTimeout(() => reject(new Error('Image upload timeout after 15 seconds')), 15000);
    });
    
    return await Promise.race([uploadPromise, timeoutPromise]);
  } catch (error) {
    console.error('Error in uploadImageToFirebase:', error);
    throw error;
  }
};

const PendingApprovalBlock: React.FC<PendingApprovalBlockProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [showKYCForm, setShowKYCForm] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [kycMessageShown, setKycMessageShown] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [kycData, setKycData] = useState<KYCData>({
    nationalIdPassport: {
      type: 'national_id',
      number: '',
      issueDate: '',
      expiryDate: '',
      frontImage: null,
      backImage: null
    },
    taxId: {
      type: 'pan',
      number: '',
      issueDate: '',
      expiryDate: ''
    }
  });

  // Custom CSS for white calendar icon in date inputs
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      input[type="date"]::-webkit-calendar-picker-indicator {
        filter: invert(1) brightness(100) saturate(100%);
        cursor: pointer;
      }
      input[type="date"]::-webkit-calendar-picker-indicator:hover {
        filter: invert(1) brightness(100) saturate(100%);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Reset message state when user changes
  React.useEffect(() => {
    if (user?.uid) {
      setKycMessageShown(false);
    }
  }, [user?.uid]);

  // Show KYC success message when user needs to complete KYC
  React.useEffect(() => {
    // Only show message if user is properly authenticated and in pending status
    if (user && user.uid && user.status === 'pending' && 
        (!user.kycDocuments || !user.kycSubmittedAt) && 
        showKYCForm && 
        !kycMessageShown) {
      
      // Use setTimeout to ensure the message shows after the component is fully rendered
      const timer = setTimeout(() => {
        toast.success('Complete your KYC Process', {
          icon: '✅',
          style: {
            background: '#10B981',
            color: 'white',
          },
          duration: 4000, // Show for 4 seconds
        });
        setKycMessageShown(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [user?.uid, user?.status, user?.kycDocuments, user?.kycSubmittedAt, showKYCForm, kycMessageShown]);

  // KYC form handling functions
  const handleInputChange = (section: keyof KYCData, field: string, value: string) => {
    setKycData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleImageUpload = (section: keyof KYCData, field: string, file: File | null) => {
    setKycData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: file
      }
    }));
  };

  const validateForm = (): boolean => {
    const { nationalIdPassport, taxId } = kycData;
    
    // Validating KYC form
    
    // Validate National ID/Passport - number is required, dates are optional
    if (!nationalIdPassport.number || nationalIdPassport.number.trim() === '') {
      toast.error('Please enter your National ID/Passport/Driving License number');
      return false;
    }
    
    // Validate Tax ID - only number is required, dates are optional
    if (!taxId.number || taxId.number.trim() === '') {
      toast.error('Please enter your Tax ID number');
      return false;
    }

    // Check if required National ID images are uploaded
    if (!nationalIdPassport.frontImage) {
      toast.error('Please upload the front image of your National ID/Passport/Driving License');
      return false;
    }

    if (!nationalIdPassport.backImage) {
      toast.error('Please upload the back image of your National ID/Passport/Driving License');
      return false;
    }
    
    // Validate dates only if they are provided
    if (nationalIdPassport.issueDate && nationalIdPassport.expiryDate) {
      const today = new Date();
      const issueDate = new Date(nationalIdPassport.issueDate);
      const expiryDate = new Date(nationalIdPassport.expiryDate);
      
      if (issueDate > today) {
        toast.error('National ID issue date cannot be in the future');
        return false;
      }
      
      if (expiryDate <= today) {
        toast.error('National ID expiry date must be in the future');
        return false;
      }
      
      if (issueDate >= expiryDate) {
        toast.error('National ID issue date must be before expiry date');
        return false;
      }
    }

    // Validate Tax ID dates if provided
    if (taxId.issueDate && taxId.expiryDate) {
      const today = new Date();
      const issueDate = new Date(taxId.issueDate);
      const expiryDate = new Date(taxId.expiryDate);
      
      if (issueDate > today) {
        toast.error('Tax ID issue date cannot be in the future');
        return false;
      }
      
      if (expiryDate <= today) {
        toast.error('Tax ID expiry date must be in the future');
        return false;
      }
      
      if (issueDate >= expiryDate) {
        toast.error('Tax ID issue date must be before expiry date');
        return false;
      }
    }
    
    // KYC form validation passed
    return true;
  };

  const handleKYCSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !user) return;
    
    setIsLoading(true);
    try {
      // Starting KYC submission
      
      // Check if Firebase storage is initialized
      if (!storage) {
        throw new Error('Firebase Storage is not initialized');
      }
      // Firebase Storage is initialized
      
      // Check if Firestore is initialized
      if (!db) {
        throw new Error('Firestore is not initialized');
      }
      // Firestore is initialized
      
      // Test Firebase storage connection
      try {
        const testRef = ref(storage, 'test-connection');
      } catch (testError) {
        console.error('Firebase storage test failed:', testError);
        throw new Error('Firebase Storage connection test failed');
      }

      // Upload images to Firebase Storage
      const imageUrls: { [key: string]: string } = {};
      let uploadSuccess = true;
      
      try {
        if (kycData.nationalIdPassport.frontImage) {
          setUploadProgress('Uploading front image...');
          // Uploading front image
          try {
            const compressedFrontImage = await compressImage(kycData.nationalIdPassport.frontImage);
            imageUrls.nationalIdFront = await uploadImageToFirebase(
              compressedFrontImage, 
              `kyc/${user.uid}/nationalId_front_${Date.now()}`
            );
            // Front image uploaded
            setUploadProgress('Front image uploaded successfully');
          } catch (frontError) {
            console.error('Front image upload failed:', frontError);
            uploadSuccess = false;
            toast.error('Front image upload failed. KYC data will be saved without images.');
          }
        } else {
          // No front image to upload
        }
        
        if (kycData.nationalIdPassport.backImage) {
          setUploadProgress('Uploading back image...');
          // Uploading back image
          try {
            const compressedBackImage = await compressImage(kycData.nationalIdPassport.backImage);
            imageUrls.nationalIdBack = await uploadImageToFirebase(
              compressedBackImage, 
              `kyc/${user.uid}/nationalId_back_${Date.now()}`
            );
            // Back image uploaded
            setUploadProgress('Back image uploaded successfully');
          } catch (backError) {
            console.error('Back image upload failed:', backError);
            uploadSuccess = false;
            toast.error('Back image upload failed. KYC data will be saved without images.');
          }
        } else {
          // No back image to upload
        }
        
        setUploadProgress('Saving KYC data...');
        // Image upload process completed
      } catch (uploadError: any) {
        console.error('Image upload process failed:', uploadError);
        setUploadProgress('');
        uploadSuccess = false;
        toast.error('Image upload process failed. KYC data will be saved without images.');
      }

      // Prepare KYC data with image URLs
      const kycDataWithImages = {
        ...kycData,
        nationalIdPassport: {
          ...kycData.nationalIdPassport,
          frontImage: imageUrls.nationalIdFront || 'pending_upload',
          backImage: imageUrls.nationalIdBack || 'pending_upload'
        }
      };

      // KYC data with images

      // Save KYC data to Firestore
      // Saving to Firestore
      
      const userRef = doc(db, 'users', user.uid);
      // User reference created
      
      // Update KYC documents and status
      await updateDoc(userRef, {
        kycDocuments: kycDataWithImages,
        kycSubmittedAt: new Date(),
        status: 'pending'
      });
      
      // KYC documents saved successfully
      
      // Show appropriate success message based on image upload status
      if (imageUrls.nationalIdFront && imageUrls.nationalIdBack) {
        toast.success('KYC documents submitted successfully! Your account is now under review.');
      } else if (imageUrls.nationalIdFront || imageUrls.nationalIdBack) {
        toast.success('KYC submitted with partial images! Please upload missing images later to complete verification.');
      } else {
        toast.success('KYC information submitted! Please upload your document images later to complete verification.');
      }
      
      setShowKYCForm(false);
              // Navigate to livetradingstudio after successful submission
        // KYC submission completed
      // Add a small delay to ensure the UI updates properly
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error: any) {
      console.error('KYC submission error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Handle specific Firebase errors
      if (error.code === 'storage/unauthorized') {
        toast.error('You do not have permission to upload files. Please contact support.');
      } else if (error.code === 'storage/quota-exceeded') {
        toast.error('Storage quota exceeded. Please try again later.');
      } else if (error.code === 'firestore/permission-denied') {
        toast.error('You do not have permission to update your profile. Please contact support.');
      } else {
        toast.error(`Failed to save KYC documents: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setIsLoading(false);
      setUploadProgress(''); // Clear progress on completion or failure
    }
  };

  // If user is not authenticated, show children (normal app flow)
  if (!user) {
    return <>{children}</>;
  }

  // If user is approved, show children (normal app flow)
  if (user.status === 'approved') {
    return <>{children}</>;
  }

  // If user is pending, check if KYC is needed
  if (user.status === 'pending') {
    // If KYC hasn't been submitted and user wants to see the form, show KYC form first
    if ((!user.kycDocuments || !user.kycSubmittedAt) && showKYCForm) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2">
          <div className="bg-gray-800 rounded-lg p-6 max-w-[95vw] w-full border border-gray-700 shadow-2xl max-h-[95vh] overflow-y-auto relative">
            {/* Close Button */}
            <button
              onClick={async () => {
                try {
                  await logout();
                  navigate('/login');
                } catch (error) {
                  navigate('/login');
                }
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
            >
              <X className="h-5 w-5" />
            </button>

                         <div className="flex items-center justify-center mb-3">
               <div className="bg-red-500/70 p-3 rounded-full" >
                 <Shield className="h-8 w-8 text-white" />
               </div>
             </div>
            
            <h2 className="text-xl font-bold text-white text-center mb-2">
              Complete KYC Verification
            </h2>
            
            <p className="text-gray-300 text-center mb-4 leading-relaxed text-xs">
              To proceed with your account approval, please complete your Know Your Customer (KYC) verification. 
              This information is required for regulatory compliance and account security.
            </p>
            
            <form onSubmit={handleKYCSubmit}>
              {/* Four Column Layout with Logo */}
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-8">
                
                {/* Column 1: Logo */}
                <div className="flex items-center justify-center bg-gray-900/50 rounded-lg p-6 border border-gray-600">
                  <div className="text-center">
                    <Logo size="xxl" />
                    <p className="text-xs text-gray-400 mt-4">
                      Live Trading Studio
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Secure Identity Verification
                    </p>
                  </div>
                </div>
                
                {/* Column 2: National ID or Passport */}
                <div className="bg-gray-700/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2 text-blue-400" />
                    National ID or Passport
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Document Type</label>
                      <select
                        value={kycData.nationalIdPassport.type}
                        onChange={(e) => handleInputChange('nationalIdPassport', 'type', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                      >
                        <option value="national_id">National ID</option>
                        <option value="passport">Passport</option>
                        <option value="driving_license">Driving License</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Document Number</label>
                      <input
                        type="text"
                        value={kycData.nationalIdPassport.number}
                        onChange={(e) => handleInputChange('nationalIdPassport', 'number', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                        placeholder="Enter document number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Issue Date</label>
                      <input
                        type="date"
                        value={kycData.nationalIdPassport.issueDate}
                        onChange={(e) => handleInputChange('nationalIdPassport', 'issueDate', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Expiry Date</label>
                      <input
                        type="date"
                        value={kycData.nationalIdPassport.expiryDate}
                        onChange={(e) => handleInputChange('nationalIdPassport', 'expiryDate', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                      />
                    </div>
                  </div>
                </div>

                {/* Column 3: Document Images (National ID/Passport) */}
                <div className="bg-gray-700/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2 text-green-400" />
                    {kycData.nationalIdPassport.type === 'national_id' && 'National ID (Required)'}
                    {kycData.nationalIdPassport.type === 'passport' && 'Passport (Required)'}
                    {kycData.nationalIdPassport.type === 'driving_license' && 'Driving License (Required)'}
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Front Image Upload */}
                    <div className="bg-gray-600/50 rounded-lg p-4 border border-gray-500">
                      <h5 className="text-xs font-medium text-gray-300 mb-2">Front Image *</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-center w-full">
                          <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-500 border-dashed rounded-lg cursor-pointer bg-gray-600 hover:bg-gray-500 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-3 pb-4">
                              {kycData.nationalIdPassport.frontImage ? (
                                <div className="text-center">
                                  <p className="text-xs text-green-400 mb-1">✓ Image uploaded</p>
                                  <p className="text-xs text-gray-300">{kycData.nationalIdPassport.frontImage.name}</p>
                                </div>
                              ) : (
                                <>
                                  <CreditCard className="w-6 h-6 mb-1 text-gray-400" />
                                  <p className="text-xs text-gray-400">Click to upload front image</p>
                                  <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                                  <p className="text-xs text-yellow-400">Large files may cause upload delays</p>
                                </>
                              )}
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => handleImageUpload('nationalIdPassport', 'frontImage', e.target.files?.[0] || null)}
                            />
                          </label>
                        </div>
                        
                        {kycData.nationalIdPassport.frontImage && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-300">
                              {kycData.nationalIdPassport.frontImage.name}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleImageUpload('nationalIdPassport', 'frontImage', null)}
                                className="text-red-400 hover:text-red-300 text-xs"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Back Image Upload */}
                    <div className="bg-gray-600/50 rounded-lg p-4 border border-gray-500">
                      <h5 className="text-xs font-medium text-gray-300 mb-2">Back Image *</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-center w-full">
                          <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-500 border-dashed rounded-lg cursor-pointer bg-gray-600 hover:bg-gray-500 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-3 pb-4">
                              {kycData.nationalIdPassport.backImage ? (
                                <div className="text-center">
                                  <p className="text-xs text-green-400 mb-1">✓ Image uploaded</p>
                                  <p className="text-xs text-gray-300">{kycData.nationalIdPassport.backImage.name}</p>
                                </div>
                              ) : (
                                <>
                                  <CreditCard className="w-6 h-6 mb-1 text-gray-400" />
                                  <p className="text-xs text-gray-400">Click to upload back image</p>
                                  <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                                  <p className="text-xs text-yellow-400">Large files may cause upload delays</p>
                                </>
                              )}
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => handleImageUpload('nationalIdPassport', 'backImage', e.target.files?.[0] || null)}
                            />
                          </label>
                        </div>
                        
                        {kycData.nationalIdPassport.backImage && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-300">
                              {kycData.nationalIdPassport.backImage.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleImageUpload('nationalIdPassport', 'backImage', null)}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Column 4: Tax ID Number */}
                <div className="bg-gray-700/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-purple-400" />
                    Tax ID Number
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Document Type</label>
                      <select
                        value={kycData.taxId.type}
                        onChange={(e) => handleInputChange('taxId', 'type', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                      >
                        <option value="pan">PAN Number (India)</option>
                        <option value="ssn">SSN (USA)</option>
                        <option value="tax_id">Tax ID (Europe/Australia)</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Document Number</label>
                      <input
                        type="text"
                        value={kycData.taxId.number}
                        onChange={(e) => handleInputChange('taxId', 'number', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                        placeholder="Enter tax ID number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Issue Date</label>
                      <input
                        type="date"
                        value={kycData.taxId.issueDate}
                        onChange={(e) => handleInputChange('taxId', 'issueDate', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Expiry Date</label>
                      <input
                        type="date"
                        value={kycData.taxId.expiryDate}
                        onChange={(e) => handleInputChange('taxId', 'expiryDate', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white text-xs rounded-lg border border-gray-500 focus:outline-none focus:border-[#2AB7A6] focus:ring-2 focus:ring-[#2AB7A6]/20"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons Section */}
              <div className="text-center pt-3 space-y-3">
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-8 py-3 bg-[#2AB7A6] text-white text-sm font-semibold rounded-lg hover:bg-[#25A395] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                  >
                    {isLoading ? (uploadProgress || 'Submitting Documents...') : 'Submit KYC Documents'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                    // Show the demo livetradingstudio with pending approval message
                        setShowKYCForm(false);
                    }}
                    className="px-8 py-3 bg-black text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-200 border border-gray-600 hover:border-gray-500"
                  >
                    Skip for Free Trial
                  </button>
                  
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await logout();
                        navigate('/login');
                      } catch (error) {
                        // If logout fails, still redirect to login
                        navigate('/login');
                      }
                    }}
                    className="px-8 py-3 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-500 transition-colors duration-200 border border-gray-500 hover:border-gray-400"
                  >
                    Back to Login Page
                  </button>
                </div>
                
                <p className="text-xs text-gray-400 mt-4">
                  You can complete this form later by logging in again
                </p>
              </div>
            </form>
          </div>
        </div>
                 );
    }

    // If KYC has been submitted, show the approval pending message
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700 shadow-2xl">
          {/* Top Logo */}
          <div className="flex items-center justify-center mb-4">
            <Logo size="xxxl" />
          </div>
          
          <h2 className="text-lg font-bold text-white text-center mb-3">
            Account Under Review
          </h2>
          
          <div className="text-center mb-4">
            <p className="text-gray-300 text-xs leading-relaxed mb-3">
              Thank you for completing your KYC verification! Your account is currently pending admin approval. 
              You cannot access the trading platform until your account is verified.
            </p>
            
            <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
              <h3 className="text-white font-semibold mb-2 text-xs">What happens next?</h3>
              <ul className="text-gray-300 text-xs space-y-1 text-left">
                <li>• Our team will review your application within 24 hours</li>
                <li>• You'll receive an email notification once approved</li>
                <li>• After approval, you can access all platform features</li>
                <li>• If rejected, you'll receive detailed feedback</li>
              </ul>
            </div>
          </div>
          
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <button
                onClick={async () => {
                  try {
                    await logout();
                    navigate('/login');
                  } catch (error) {
                    // If logout fails, still redirect to login
                    navigate('/login');
                  }
                }}
                className="px-6 py-3 bg-[#2AB7A6] text-white text-xs rounded-lg hover:bg-[#25A395] transition-colors duration-200 font-medium"
              >
                Return to Login
              </button>
              
              <button
                        onClick={() => {
          // Navigate to livetradingstudio for free trial access
                   navigate('/');
                 }}
                className="px-6 py-3 bg-black text-white text-xs rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium border border-gray-600 hover:border-gray-500"
              >
                Free Trial Access
              </button>
            </div>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-gray-400 text-xs">
              Need help? Contact support at{' '}
              <a href="mailto:support@livetradingstudio.com" className="text-[#2AB7A6] hover:underline">
                support@livetradingstudio.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If user is rejected, show blocking popup
  if (user.status === 'rejected') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-8 max-w-lg w-full border border-gray-700 shadow-2xl">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-red-500/20 p-4 rounded-full">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white text-center mb-4">
            Account Rejected
          </h2>
          
          <p className="text-gray-300 text-lg text-center mb-6 leading-relaxed">
            Your account application has been rejected. Please contact our support team 
            to understand the reason and discuss next steps.
          </p>
          
          <div className="text-center">
            <button
              onClick={async () => {
                try {
                  await logout();
                  navigate('/login');
                } catch (error) {
                  // If logout fails, still redirect to login
                  navigate('/login');
                }
              }}
              className="px-8 py-3 bg-[#2AB7A6] text-white rounded-lg hover:bg-[#25A395] transition-colors duration-200 font-medium"
            >
              Return to Login
            </button>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Contact support: {' '}
              <a href="mailto:support@livetradingstudio.com" className="text-[#2AB7A6] hover:underline">
                support@livetradingstudio.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If user is suspended, show blocking popup
  if (user.status === 'suspended') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-8 max-w-lg w-full border border-gray-700 shadow-2xl">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-orange-500/20 p-4 rounded-full">
              <AlertCircle className="h-12 w-12 text-orange-500" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white text-center mb-4">
            Account Suspended
          </h2>
          
          <p className="text-gray-300 text-lg text-center mb-6 leading-relaxed">
            Your account has been suspended. Please contact our support team immediately 
            to understand the reason and resolve this issue.
          </p>
          
          <div className="text-center">
            <button
              onClick={async () => {
                try {
                  await logout();
                  navigate('/login');
                } catch (error) {
                  // If logout fails, still redirect to login
                  navigate('/login');
                }
              }}
              className="px-8 py-3 bg-[#2AB7A6] text-white rounded-lg hover:bg-[#25A395] transition-colors duration-200 font-medium"
            >
              Return to Login
            </button>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Urgent support: {' '}
              <a href="mailto:support@livetradingstudio.com" className="text-[#2AB7A6] hover:underline">
                support@livetradingstudio.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Fallback - show children for any other status
  return <>{children}</>;
};

export default PendingApprovalBlock;
